import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';
import {
  authApi,
  itemApi,
  orgApi,
  partyApi,
  receiptsApi,
  refApi,
  salesApi,
  auditApi,
  type AuditQuery,
  type ItemListQuery,
  type PartyListQuery,
  type ReceiptListQuery,
  type SalesListQuery,
} from './endpoints';
import type { InvoiceKind } from './types';

/**
 * Query keys: everything that belongs to an organization lives under ['org', orgId, …], so
 * switching or leaving an organization can drop its cache in one call.
 */
export const queryKeys = {
  me: ['me'] as const,
  org: (orgId: string) => ['org', orgId] as const,
  ref: ['ref'] as const,
};

const REFERENCE_STALE = 60 * 60 * 1000; // ETA code tables change with snapshots, not per request

export const meQuery = queryOptions({
  queryKey: queryKeys.me,
  queryFn: ({ signal }) => authApi.me(signal),
  staleTime: 60_000,
  retry: false,
});

export const organizationQuery = (orgId: string) =>
  queryOptions({
    queryKey: [...queryKeys.org(orgId), 'profile'],
    queryFn: ({ signal }) => orgApi.get(orgId, signal),
  });

export const accessQuery = (orgId: string) =>
  queryOptions({
    queryKey: [...queryKeys.org(orgId), 'access'],
    queryFn: ({ signal }) => orgApi.access(orgId, signal),
    staleTime: 5 * 60_000,
  });

export const branchesQuery = (orgId: string) =>
  queryOptions({
    queryKey: [...queryKeys.org(orgId), 'branches'],
    queryFn: ({ signal }) => orgApi.branches(orgId, signal),
  });

export const taxesQuery = (orgId: string) =>
  queryOptions({
    queryKey: [...queryKeys.org(orgId), 'taxes'],
    queryFn: ({ signal }) => orgApi.taxes(orgId, signal),
  });

/** The organization's activity codes (primary first). */
export const activityCodesQuery = (orgId: string) =>
  queryOptions({
    queryKey: [...queryKeys.org(orgId), 'activity-codes'],
    queryFn: ({ signal }) => orgApi.activityCodes(orgId, signal),
  });

export const orgCurrenciesQuery = (orgId: string) =>
  queryOptions({
    queryKey: [...queryKeys.org(orgId), 'currencies'],
    queryFn: ({ signal }) => orgApi.currencies(orgId, signal),
  });

export const categoriesQuery = (orgId: string, scope: 'item' | 'party') =>
  queryOptions({
    queryKey: [...queryKeys.org(orgId), 'categories', scope],
    queryFn: ({ signal }) => orgApi.categories(orgId, scope, signal),
  });

export const nextNumberQuery = (orgId: string, kind: InvoiceKind) =>
  queryOptions({
    queryKey: [...queryKeys.org(orgId), 'sales', 'next-number', kind],
    queryFn: ({ signal }) => salesApi.nextNumber(orgId, kind, signal),
    staleTime: 0,
  });

export const taxTypesQuery = queryOptions({
  queryKey: [...queryKeys.ref, 'tax-types'],
  queryFn: ({ signal }) => refApi.taxTypes(signal),
  staleTime: REFERENCE_STALE,
});

export const currenciesQuery = queryOptions({
  queryKey: [...queryKeys.ref, 'currencies'],
  queryFn: ({ signal }) => refApi.currencies(signal),
  staleTime: REFERENCE_STALE,
});

export const paymentMethodsQuery = queryOptions({
  queryKey: [...queryKeys.ref, 'payment-methods'],
  queryFn: ({ signal }) => refApi.paymentMethods(signal),
  staleTime: REFERENCE_STALE,
});

export const weightUnitsQuery = queryOptions({
  queryKey: [...queryKeys.ref, 'weight-unit-types'],
  queryFn: ({ signal }) => refApi.weightUnitTypes(signal),
  staleTime: REFERENCE_STALE,
});

export const countriesQuery = queryOptions({
  queryKey: [...queryKeys.ref, 'countries'],
  queryFn: ({ signal }) => refApi.countries(signal),
  staleTime: REFERENCE_STALE,
});

export const unitTypesQuery = (q: string) =>
  queryOptions({
    queryKey: [...queryKeys.ref, 'unit-types', q],
    queryFn: ({ signal }) => refApi.unitTypes(q, signal),
    staleTime: REFERENCE_STALE,
    placeholderData: keepPreviousData,
  });

export const activityCodeSearchQuery = (q: string) =>
  queryOptions({
    queryKey: [...queryKeys.ref, 'activity-codes', q],
    queryFn: ({ signal }) => refApi.activityCodes(q, signal),
    staleTime: REFERENCE_STALE,
    placeholderData: keepPreviousData,
  });

export const partiesQuery = (orgId: string, query: PartyListQuery) =>
  queryOptions({
    queryKey: [...queryKeys.org(orgId), 'parties', 'list', query],
    queryFn: ({ signal }) => partyApi.list(orgId, query, signal),
    placeholderData: keepPreviousData,
  });

export const partyQuery = (orgId: string, id: string) =>
  queryOptions({
    queryKey: [...queryKeys.org(orgId), 'parties', 'detail', id],
    queryFn: ({ signal }) => partyApi.get(orgId, id, signal),
  });

export const itemsQuery = (orgId: string, query: ItemListQuery) =>
  queryOptions({
    queryKey: [...queryKeys.org(orgId), 'items', 'list', query],
    queryFn: ({ signal }) => itemApi.list(orgId, query, signal),
    placeholderData: keepPreviousData,
  });

export const itemQuery = (orgId: string, id: string) =>
  queryOptions({
    queryKey: [...queryKeys.org(orgId), 'items', 'detail', id],
    queryFn: ({ signal }) => itemApi.get(orgId, id, signal),
  });

export const receiptsListQuery = (orgId: string, query: ReceiptListQuery) =>
  queryOptions({
    queryKey: [...queryKeys.org(orgId), 'receipts', 'list', query],
    queryFn: ({ signal }) => receiptsApi.list(orgId, query, signal),
    placeholderData: keepPreviousData,
  });

export const receiptQuery = (orgId: string, id: string) =>
  queryOptions({
    queryKey: [...queryKeys.org(orgId), 'receipts', 'detail', id],
    queryFn: ({ signal }) => receiptsApi.get(orgId, id, signal),
  });

/** The organization's in-service POS devices (receipt editor, WP-8.9c). */
export const posDevicesQuery = (orgId: string) =>
  queryOptions({
    queryKey: [...queryKeys.org(orgId), 'pos-devices'],
    queryFn: ({ signal }) => receiptsApi.devices(orgId, signal),
  });

export const receiptNextNumberQuery = (orgId: string, posDeviceId: string) =>
  queryOptions({
    queryKey: [...queryKeys.org(orgId), 'receipts', 'next-number', posDeviceId],
    queryFn: ({ signal }) => receiptsApi.nextNumber(orgId, posDeviceId, signal),
    enabled: Boolean(posDeviceId),
    staleTime: 0,
  });

export const salesListQuery = (orgId: string, query: SalesListQuery) =>
  queryOptions({
    queryKey: [...queryKeys.org(orgId), 'sales', 'list', query],
    queryFn: ({ signal }) => salesApi.list(orgId, query, signal),
    placeholderData: keepPreviousData,
  });

export const salesDocumentQuery = (orgId: string, id: string) =>
  queryOptions({
    queryKey: [...queryKeys.org(orgId), 'sales', 'detail', id],
    queryFn: ({ signal }) => salesApi.get(orgId, id, signal),
  });

/** Block 11 (D-3): the invoices a note may reference — the note editor's add field (step 1). */
export const noteSourcesQuery = (
  orgId: string,
  params: { kind: InvoiceKind; q?: string; receiverPartyId?: string; limit?: number },
) =>
  queryOptions({
    queryKey: [...queryKeys.org(orgId), 'sales', 'note-sources', params],
    queryFn: ({ signal }) => salesApi.noteSources(orgId, params, signal),
    placeholderData: keepPreviousData,
  });

/** Block 11 (D-9, D-11): one referenced invoice's lines and cap — the reference-lines sheet (step 3). */
export const noteBasisQuery = (orgId: string, id: string, kind: InvoiceKind) =>
  queryOptions({
    queryKey: [...queryKeys.org(orgId), 'sales', 'note-basis', id, kind],
    queryFn: ({ signal }) => salesApi.noteBasis(orgId, id, kind, signal),
  });

export const auditQuery = (orgId: string, query: AuditQuery) =>
  queryOptions({
    queryKey: [...queryKeys.org(orgId), 'audit', query],
    queryFn: ({ signal }) => auditApi.list(orgId, query, signal),
    placeholderData: keepPreviousData,
  });

export function useMe() {
  return useQuery(meQuery);
}
